import { snakeCase } from "lodash";
import {
  ChangeEvent,
  SyntheticEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { CaseDocumentWithPresignedUrl } from "pwapi";

import { fetchCaseswithDocuments } from "shared/actions/casesActions";
import { CaseListDirection } from "shared/reducers/casesReducer";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import DateInput from "components/customInputs/DateInput";
import DateRangeInput from "components/customInputs/DateRangeInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";
import {
  reduceFiltersForSearchParams,
  reduceFiltersToApi,
  resetFilters,
} from "components/filtersUtils";
import { SocketMessageContext } from "components/gates/SocketMessageProvider";
import { useAppSelector } from "store/hooks";

import { FilterObject } from "../caseList/casesFilters/CaseFilterForm";
import FiltersView from "../FiltersView";

type ReportFiltersFormProps = {
  sortBy: { id: string; desc?: boolean }[];
  pageSize: number;
  pageIndex: number;
  listLoading?: boolean;
  totalCount: number;
  setPage: Function;
  reportType: "report" | "requisition";
  toggleAllRowsExpanded?: Function;
};

const DoubleUnderscoreFilterMatch = {
  diagnosesReportSignedAfter: "diagnoses__report_signed_after",
  diagnosesReportSignedBefore: "diagnoses__report_signed_before",
  patientClinicName: "patient__clinic__name",
  patientDateOfBirth: "patient__date_of_birth",
};

const ReportFiltersForm = ({
  sortBy,
  pageSize,
  pageIndex,
  listLoading,
  totalCount,
  setPage,
  reportType,
  toggleAllRowsExpanded,
}: ReportFiltersFormProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { setSocketMessage } = useContext(SocketMessageContext);

  const user = useAppSelector(({ auth }) => auth.user);
  const features = useAppSelector(({ sys }) => sys.features);

  const usp = new URLSearchParams(location.search);
  const initialFilters = useMemo(() => {
    return [...usp.entries()].reduce(
      (acc: FilterObject, [id, value]) => {
        if (value) {
          acc[id] = Array.isArray(acc[id]) ? value.split(",") : value;
        }
        return acc;
      },
      { category: [] }
    );
  }, [location]);

  const setInitialFilters = (filters: FilterObject) => {
    return Object.keys(filters).reduce((result: FilterObject, key) => {
      result[key] = initialFilters[key] || "";
      return result;
    }, {});
  };

  const [filters, setFilters] = useState<FilterObject>(
    setInitialFilters({
      accession: "",
      patientName: "",
      patientDateOfBirth: "",
      orderingDoctorName: "",
      activeAssignmentName: "",
      patientClinicName: "",
      collectedDateAfter: "",
      collectedDateBefore: "",
      receivedDateAfter: "",
      receivedDateBefore: "",
      diagnosesReportSignedBefore: "",
      diagnosesReportSignedAfter: "",
    })
  );

  const [checkboxFilters, setCheckboxFilters] = useState<{
    [key: string]: boolean | undefined;
  }>({
    filterUnviewedDocuments: !!initialFilters.filterUnviewedDocuments,
  });

  const [disableBulkReport, setDisableBulkReport] = useState<boolean>(false);

  const currReportType = useRef(reportType);

  useEffect(() => {
    if (reportType !== currReportType.current) {
      setPage(0);
      currReportType.current = reportType;
    }
  }, [reportType]);

  useEffect(() => {
    filterReports();
  }, [location.pathname, usp.toString(), pageSize, pageIndex, reportType]);

  useEffect(() => {
    setSearchParams(pageIndex);
  }, [sortBy, pageIndex]);

  const onChangeFilter = (filter: string, value: string) => {
    setFilters({
      ...filters,
      [filter]: value,
    });
  };

  const onChangeDateRange = (values: { [key: string]: Date }) => {
    const newState = Object.assign({}, filters, values);
    setFilters(newState);
  };

  const getSortByQuery = useCallback(() => {
    if (!(sortBy && sortBy.length)) {
      return {};
    }
    const sortDirection = sortBy[0].desc ? "desc" : "asc";
    return {
      ...(sortDirection !== CaseListDirection.Asc
        ? {
            sortDirection: sortDirection,
          }
        : {}),
      ...(sortBy[0].id !== "collected_date"
        ? {
            sortColumn: sortBy[0].id,
          }
        : {}),
    };
  }, [sortBy]);

  const checkFiltersChanged = (filters: FilterObject) => {
    return Object.keys(filters).some((s) =>
      Array.isArray(filters[s]) ? filters[s].length > 0 : filters[s] !== ""
    );
  };

  const setSearchParams = useCallback(
    (pageIndex: number, isReset?: boolean) => {
      const params: any = {
        ...reduceFiltersForSearchParams(
          isReset ? resetFilters(filters) : filters
        ),
        ...(pageIndex !== 0 && { page: pageIndex + 1 }),
        ...getSortByQuery(),
        ...(!isReset &&
          checkboxFilters.filterUnviewedDocuments && {
            filterUnviewedDocuments: "true",
          }),
      };

      navigate(
        `${location.pathname}?${new URLSearchParams(params).toString()}`,
        {
          replace: true,
        }
      );
    },
    [filters, checkboxFilters, sortBy, pageIndex, location]
  );

  const filterReports = useCallback(() => {
    const apiState = {
      ...reduceFiltersToApi(filters),
      page: pageIndex + 1,
      paginationPerPage: pageSize,
      sortColumn: sortBy.length ? sortBy[0].id || "" : "collected_date",
      sortDirection:
        sortBy.length && sortBy[0].desc
          ? CaseListDirection.Desc
          : CaseListDirection.Asc,
      queue: "all",
      category:
        reportType === "report"
          ? CaseDocumentWithPresignedUrl.category.CASE_REPORT
          : CaseDocumentWithPresignedUrl.category.REQUISITION_FORM,
      ...(checkboxFilters.filterUnviewedDocuments && {
        hasBeenViewed: "false",
      }),
    };
    dispatch(fetchCaseswithDocuments(apiState));
    toggleAllRowsExpanded && toggleAllRowsExpanded(false);
  }, [filters, checkboxFilters, sortBy, pageIndex, pageSize, reportType]);

  const getZipFile = () => {
    const apiFilters = reduceFiltersToApi(filters);
    const snakeCaseFilters = Object.keys(apiFilters).reduce((acc, key) => {
      const keyName =
        DoubleUnderscoreFilterMatch[
          key as keyof typeof DoubleUnderscoreFilterMatch
        ] || snakeCase(key);
      return { ...acc, [keyName]: apiFilters[key] };
    }, {});
    const apiState = JSON.stringify({
      type: "websockets.receive",
      action: "downloads",
      filters: {
        ...snakeCaseFilters,
        category:
          reportType === "report"
            ? CaseDocumentWithPresignedUrl.category.CASE_REPORT
            : CaseDocumentWithPresignedUrl.category.REQUISITION_FORM,
        ...(checkboxFilters.filterUnviewedDocuments && {
          has_been_viewed: "false",
        }),
      },
    });

    setSocketMessage(apiState);
  };

  const onHandleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    setPage(0);
    setSearchParams(0);
    setDisableBulkReport(false);
  };

  const onCheckFiltersChanged = useCallback(() => {
    return (
      checkFiltersChanged(filters) ||
      checkboxFilters.filterUnviewedDocuments ||
      pageIndex !== 0
    );
  }, [filters, checkboxFilters, sortBy, pageIndex]);

  const onResetFilter = () => {
    setFilters(resetFilters(filters));
    setCheckboxFilters({ filterUnviewedDocuments: false });
    setPage(0);
    setSearchParams(0, true);
    setDisableBulkReport(false);
  };

  return (
    <>
      <FiltersView
        onSubmit={onHandleSubmit}
        submitLabel={`Search ${reportType === "report" ? "Reports" : "Forms"}`}
        onReset={onResetFilter}
        onCheckFiltersChanged={onCheckFiltersChanged}
      >
        <div className="filter__section">
          <TextInput
            id="accession"
            name="accession"
            value={filters.accession as string}
            label="Accession"
            onChange={(e: any) => onChangeFilter("accession", e.target.value)}
            onClear={() => onChangeFilter("accession", "")}
          />
          <TextInput
            id="patientName"
            name="patientName"
            value={filters.patientName as string}
            label="Patient"
            onChange={(e: any) => onChangeFilter("patientName", e.target.value)}
            onClear={() => onChangeFilter("patientName", "")}
          />
          <CustomInput
            label="Patient Date of Birth"
            id="patientDateOfBirth"
            onClear={() => onChangeFilter("patientDateOfBirth", "")}
          >
            <DateInput
              value={filters.patientDateOfBirth as string}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onChangeFilter("patientDateOfBirth", e.target.value)
              }
              placeholder=" "
              showPickerOnFocus
            />
          </CustomInput>
          <DateRangeInput
            id="collectedDate"
            label="Collected Date"
            values={{
              from: {
                id: "collectedDateAfter",
                name: "collected_date_after",
                value: filters.collectedDateAfter as string,
              },
              to: {
                id: "collectedDateBefore",
                name: "collected_date_before",
                value: filters.collectedDateBefore as string,
              },
            }}
            onChangeDate={onChangeDateRange}
            isClearable
          />
          <DateRangeInput
            id="receivedDate"
            label="Received Date"
            values={{
              from: {
                id: "receivedDateAfter",
                name: "received_date_after",
                value: filters.receivedDateAfter as string,
              },
              to: {
                id: "receivedDateBefore",
                name: "received_date_before",
                value: filters.receivedDateBefore as string,
              },
            }}
            onChangeDate={onChangeDateRange}
            isClearable
          />
          <DateRangeInput
            id="reportSignedDate"
            label="Report Signed Date"
            values={{
              from: {
                id: "diagnosesReportSignedAfter",
                name: "diagnoses__report_signed_after",
                value: filters.diagnosesReportSignedAfter as string,
              },
              to: {
                id: "diagnosesReportSignedBefore",
                name: "diagnoses__report_signed_before",
                value: filters.diagnosesReportSignedBefore as string,
              },
            }}
            onChangeDate={onChangeDateRange}
            isClearable
          />
          <SelectInput
            id="patientClinicName"
            label="Clinic"
            value={filters.patientClinicName as string}
            defaultLabel="All Clinics"
            options={
              user?.clinics
                ? user.clinics.map((clinic) => {
                    return {
                      label: clinic.name,
                      value: clinic.name,
                    };
                  })
                : []
            }
            onChange={(e: any) => {
              onChangeFilter("patientClinicName", e.target.value);
            }}
          />
          <TextInput
            id="activeAssignmentName"
            name="activeAssignmentName"
            value={filters.activeAssignmentName as string}
            label="Active Assignment"
            onChange={(e: any) =>
              onChangeFilter("activeAssignmentName", e.target.value)
            }
            onClear={() => onChangeFilter("activeAssignmentName", "")}
          />
          <TextInput
            id="orderingDoctorName"
            name="orderingDoctorName"
            value={filters.orderingDoctorName as string}
            label="Ordering Provider"
            onChange={(e: any) =>
              onChangeFilter("orderingDoctorName", e.target.value)
            }
            onClear={() => onChangeFilter("orderingDoctorName", "")}
          />

          <div className="CustomInput CustomInput__container CustomInput__container--checkbox">
            <label className="checkboxFilter">
              <input
                type="checkbox"
                checked={checkboxFilters.filterUnviewedDocuments}
                onChange={() => {
                  setCheckboxFilters({
                    ...checkboxFilters,
                    filterUnviewedDocuments:
                      !checkboxFilters.filterUnviewedDocuments,
                  });
                }}
              />
              &nbsp;Unviewed Documents
            </label>
          </div>
        </div>
      </FiltersView>
      {features?.bulkReportDownload &&
        !listLoading &&
        reportType === "report" && (
          <div className="ViewerFilterForm__controls">
            <PathologyWatchButton
              disabled={
                disableBulkReport || totalCount > 5000 || totalCount <= 0
              }
              onClick={() => {
                getZipFile();
                setDisableBulkReport(true);
              }}
            >
              <>
                Download {totalCount || 0} Case Report
                {totalCount === 1 ? "" : "s"}
              </>
            </PathologyWatchButton>
            {totalCount > 5000 ? (
              <span className="ViewerFilterForm__controls--default">
                Cannot download more than 5000 reports at once
              </span>
            ) : disableBulkReport ? (
              <span className="ViewerFilterForm__controls--default">
                Download request initiated
              </span>
            ) : undefined}
          </div>
        )}
    </>
  );
};

export default ReportFiltersForm;
